import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { GlobalModeOverlay } from "@/components/auth/GlobalModeOverlay";
import { BillingDataGate } from "@/components/billing/BillingDataGate";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import { usePrefetchCategories } from "@/hooks/useActiveCategories";
import { TrackingScripts } from "@/components/TrackingScripts";
import { CookieConsent } from "@/components/CookieConsent";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import PageLoader from "@/components/PageLoader";

// ── Public pages ──
const Index = lazy(() => import("./pages/Index"));
const Professionals = lazy(() => import("./pages/Professionals"));
const ProfessionalProfile = lazy(() => import("./pages/ProfessionalProfile"));
const CompanyProfile = lazy(() => import("./pages/CompanyProfile"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Search = lazy(() => import("./pages/Search"));
const Pricing = lazy(() => import("./pages/Pricing"));
const VisibilityPackages = lazy(() => import("./pages/VisibilityPackages"));
const SubscriptionPlans = lazy(() => import("./pages/SubscriptionPlans"));
const BecomeProfessional = lazy(() => import("./pages/BecomeProfessional"));
const Partners = lazy(() => import("./pages/Partners"));
const PartnerDetail = lazy(() => import("./pages/PartnerDetail"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Install = lazy(() => import("./pages/Install"));
const AboutUs = lazy(() => import("./pages/AboutUs"));

// ── Static / legal pages ──
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));

const Maintenance = lazy(() => import("./pages/Maintenance"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const Changelog = lazy(() => import("./pages/Changelog"));
const NotFound = lazy(() => import("./pages/NotFound"));

// ── Auth pages ──
const Register = lazy(() => import("./pages/auth/Register"));
const Login = lazy(() => import("./pages/auth/Login"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));

// ── Dashboard pages ──
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Subscription = lazy(() => import("./pages/dashboard/Subscription"));
const ProfileEdit = lazy(() => import("./pages/dashboard/ProfileEdit"));
const EventsManage = lazy(() => import("./pages/dashboard/EventsManage"));
const EventCreate = lazy(() => import("./pages/dashboard/EventCreate"));
const EventParticipants = lazy(() => import("./pages/dashboard/EventParticipants"));
const SavedSearches = lazy(() => import("./pages/dashboard/SavedSearches"));
const ReviewsManage = lazy(() => import("./pages/dashboard/ReviewsManage"));
const Affiliate = lazy(() => import("./pages/dashboard/Affiliate"));
const Creator = lazy(() => import("./pages/dashboard/Creator"));
const Earnings = lazy(() => import("./pages/dashboard/Earnings"));
const Preferences = lazy(() => import("./pages/dashboard/Preferences"));
const UserNotifications = lazy(() => import("./pages/dashboard/Notifications"));
const CompanyJobs = lazy(() => import("./pages/dashboard/CompanyJobs"));
const JobCreate = lazy(() => import("./pages/dashboard/JobCreate"));
const JobApplications = lazy(() => import("./pages/dashboard/JobApplications"));

// ── Admin pages ──
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminCoupons = lazy(() => import("./pages/admin/Coupons"));
const AdminBadges = lazy(() => import("./pages/admin/Badges"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminSubscriptions = lazy(() => import("./pages/admin/Subscriptions"));
const AdminEvents = lazy(() => import("./pages/admin/Events"));
const AdminJobs = lazy(() => import("./pages/admin/Jobs"));
const AdminPayouts = lazy(() => import("./pages/admin/Payouts"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminCategories = lazy(() => import("./pages/admin/Categories"));
const AdminBlockedIPs = lazy(() => import("./pages/admin/BlockedIPs"));
const AdminBlog = lazy(() => import("./pages/admin/Blog"));
const AdminNotifications = lazy(() => import("./pages/admin/Notifications"));
const AdminReports = lazy(() => import("./pages/admin/Reports"));
const AdminPartnerships = lazy(() => import("./pages/admin/Partnerships"));
const ScreeningAdmin = lazy(() => import("./pages/admin/ScreeningAdmin"));
const CreatorReview = lazy(() => import("./pages/admin/CreatorReview"));
const ReviewsModeration = lazy(() => import("./pages/admin/ReviewsModeration"));
const Monitoring = lazy(() => import("./pages/admin/Monitoring"));
const AdminUserInsights = lazy(() => import("./pages/admin/UserInsights"));
const AdminBugReports = lazy(() => import("./pages/admin/BugReports"));
const AdminOrganizations = lazy(() => import("./pages/admin/Organizations"));

const queryClient = new QueryClient();

const AffiliateTracker = () => {
  useAffiliateTracking();
  return null;
};

const CategoryPrefetcher = () => {
  usePrefetchCategories();
  return null;
};

const UnhandledRejectionCatcher = () => {
  React.useEffect(() => {
    const handler = (e: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", e.reason);
      e.preventDefault();
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <UnhandledRejectionCatcher />
      <Toaster />
      <BrowserRouter>
        <AffiliateTracker />
        <CategoryPrefetcher />
        <TrackingScripts />
        <AuthProvider>
          <GlobalModeOverlay />
          <BillingDataGate>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/maintenance" element={<Maintenance />} />
                <Route path="/coming-soon" element={<ComingSoon />} />
                <Route path="/" element={<Index />} />
                <Route path="/auth/register" element={<Register />} />
                <Route path="/auth/login" element={<Login />} />
                <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                {/* Onboarding - requires auth */}
                <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
                {/* Protected Dashboard Routes */}
                <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                <Route path="/dashboard/subscription" element={<RequireAuth><Subscription /></RequireAuth>} />
                <Route path="/dashboard/profile-edit" element={<RequireAuth><ProfileEdit /></RequireAuth>} />
                <Route path="/dashboard/events" element={<RequireAuth><EventsManage /></RequireAuth>} />
                <Route path="/dashboard/events/create" element={<RequireAuth><EventCreate /></RequireAuth>} />
                <Route path="/dashboard/events/:id/edit" element={<RequireAuth><EventCreate /></RequireAuth>} />
                <Route path="/dashboard/events/:id/participants" element={<RequireAuth><EventParticipants /></RequireAuth>} />
                <Route path="/dashboard/searches" element={<RequireAuth><SavedSearches /></RequireAuth>} />
                <Route path="/dashboard/reviews" element={<RequireAuth><ReviewsManage /></RequireAuth>} />
                <Route path="/dashboard/affiliate" element={<RequireAuth><Affiliate /></RequireAuth>} />
                <Route path="/dashboard/creator" element={<RequireAuth><Creator /></RequireAuth>} />
                <Route path="/dashboard/earnings" element={<RequireAuth><Earnings /></RequireAuth>} />
                <Route path="/dashboard/preferences" element={<RequireAuth><Preferences /></RequireAuth>} />
                <Route path="/dashboard/notifications" element={<RequireAuth><UserNotifications /></RequireAuth>} />
                <Route path="/dashboard/company/jobs" element={<RequireAuth><CompanyJobs /></RequireAuth>} />
                <Route path="/dashboard/company/jobs/create" element={<RequireAuth><JobCreate /></RequireAuth>} />
                <Route path="/dashboard/company/jobs/:id/edit" element={<RequireAuth><JobCreate /></RequireAuth>} />
                <Route path="/dashboard/company/jobs/:id/applications" element={<RequireAuth><JobApplications /></RequireAuth>} />
                
                {/* Protected Admin Routes */}
                <Route path="/admin" element={<RequireAuth requireAdmin><AdminDashboard /></RequireAuth>} />
                <Route path="/admin/dashboard" element={<RequireAuth requireAdmin><AdminDashboard /></RequireAuth>} />
                <Route path="/admin/notifications" element={<RequireAuth requireAdmin><AdminNotifications /></RequireAuth>} />
                <Route path="/admin/partnerships" element={<RequireAuth requireAdmin><AdminPartnerships /></RequireAuth>} />
                <Route path="/admin/reports" element={<RequireAuth requireAdmin><AdminReports /></RequireAuth>} />
                <Route path="/admin/users" element={<RequireAuth requireAdmin><AdminUsers /></RequireAuth>} />
                <Route path="/admin/coupons" element={<RequireAuth requireAdmin><AdminCoupons /></RequireAuth>} />
                <Route path="/admin/badges" element={<RequireAuth requireAdmin><AdminBadges /></RequireAuth>} />
                <Route path="/admin/analytics" element={<RequireAuth requireAdmin><AdminAnalytics /></RequireAuth>} />
                <Route path="/admin/events" element={<RequireAuth requireAdmin><AdminEvents /></RequireAuth>} />
                <Route path="/admin/jobs" element={<RequireAuth requireAdmin><AdminJobs /></RequireAuth>} />
                <Route path="/admin/payouts" element={<RequireAuth requireAdmin><AdminPayouts /></RequireAuth>} />
                <Route path="/admin/subscriptions" element={<RequireAuth requireAdmin><AdminSubscriptions /></RequireAuth>} />
                <Route path="/admin/settings" element={<RequireAuth requireAdmin><AdminSettings /></RequireAuth>} />
                <Route path="/admin/screening" element={<RequireAuth requireAdmin><ScreeningAdmin /></RequireAuth>} />
                <Route path="/admin/monitoring" element={<RequireAuth requireAdmin><Monitoring /></RequireAuth>} />
                <Route path="/admin/reviews" element={<RequireAuth requireAdmin><ReviewsModeration /></RequireAuth>} />
                <Route path="/admin/creator-review" element={<RequireAuth requireAdmin><CreatorReview /></RequireAuth>} />
                <Route path="/admin/categories" element={<RequireAuth requireAdmin><AdminCategories /></RequireAuth>} />
                <Route path="/admin/blog" element={<RequireAuth requireAdmin><AdminBlog /></RequireAuth>} />
                <Route path="/admin/blocked-ips" element={<RequireAuth requireAdmin><AdminBlockedIPs /></RequireAuth>} />
                <Route path="/admin/user-insights" element={<RequireAuth requireAdmin><AdminUserInsights /></RequireAuth>} />
                <Route path="/admin/bug-reports" element={<RequireAuth requireAdmin><AdminBugReports /></RequireAuth>} />
                <Route path="/admin/organizations" element={<RequireAuth requireAdmin><AdminOrganizations /></RequireAuth>} />
                
                {/* Public Routes */}
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/visibility-packages" element={<VisibilityPackages />} />
                <Route path="/subscription-plans" element={<SubscriptionPlans />} />
                <Route path="/become-professional" element={<BecomeProfessional />} />
                <Route path="/professional/:slug" element={<ProfessionalProfile />} />
                <Route path="/company/:slug" element={<CompanyProfile />} />
                <Route path="/professionals" element={<Professionals />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/partners/:id" element={<PartnerDetail />} />
                <Route path="/search" element={<Search />} />
                <Route path="/events" element={<Events />} />
                <Route path="/event/:slug" element={<EventDetail />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/jobs/:slug" element={<JobDetail />} />
                {/* Sitemap servita dinamicamente dall'edge function generate-sitemap */}
                {/* URL canonico dichiarato in robots.txt — niente più route SPA per /sitemap.xml */}
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/install" element={<Install />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/changelog" element={<Changelog />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BillingDataGate>
          <CookieConsent />
          <PWAInstallBanner />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
